// BULK plan-story fixtures for #5136 — a 100+ row corpus the Custom plans list
// (and the dashboard's other `plan list` tabs/pager) reads as a back-end, so the
// second page actually exists in dev and every `PlanBucket` value is reachable
// on at least one row.
//
// Why next door, not in `plan-stories-mocks.ts`/`plan-story-custom-mocks.ts`:
// those files describe plans by hand, one row per plan, and they sit at 290+
// lines against the 300-line ceiling `mock-size.test.ts` enforces. Bulk rows are
// GENERATED — every id, timestamp and group built with `Array.from(...)` — so
// the file stays small even at 100+ rows.
//
// Variants:
//   * cover every `PlanBucket` value at least once;
//   * `updatedIso` values are OLDER than every hand-written story in
//     `plan-stories-mocks.ts` (their newest is 2026-07-22) so those sort first;
//   * ids never collide with `plan_mock01`/`plan_mock_done` — reserved for the
//     canonical stories and `epic-stop.spec.ts` paths.
//
// No module-scope `.push()` here (GENERATOR shape is the convention the other
// two plan-story files follow; this file holds no mutable state).

import {
  type PlanGroupHold,
  type PlanInitiator,
  type PlanOwner,
  type PlanProjectionPullRequest,
  type PlanProjectionTask,
  PlanStatus,
  planGroupDisposition,
} from '@developerz/domain';
import { at } from './mock-session';
import type { StoryGroup } from './plan-leg-mocks';
import type { PlanStory } from './plan-stories-mocks';

const REPO = 'developerz-ai/bulk-fixtures';
const INITIATED_BY: PlanInitiator = 'user';
// null-owner is the common case (#4477) — PlanOwnerSchema is itself
// non-nullable, so the field type carries the null.
const OWNER: PlanOwner | null = null;

/**
 * Same `group()` idiom as `plan-story-custom-mocks.ts` — `REPO` is a module
 * constant, so it lives on the returned object, NOT on `input`.
 */
function group(input: {
  groupId: string;
  position: number;
  title: string;
  status: StoryGroup['status'];
  dependsOn: readonly string[];
  task: PlanProjectionTask | null;
  pullRequest: PlanProjectionPullRequest | null;
  heldOn?: PlanGroupHold | null;
}): StoryGroup {
  const verdict = planGroupDisposition({
    groupStatus: input.status,
    task:
      input.task === null
        ? null
        : {
            status: input.task.status,
            pullRequestNumber: input.task.pullRequestNumber,
            outcomeKind: 'pr',
            noWorkSettled: false,
            opsActionRefused: false,
          },
    pullRequestState: input.pullRequest?.state ?? null,
  });
  const { heldOn = null, dependsOn, ...rest } = input;
  return {
    ...rest,
    repoFullName: REPO,
    dependsOn: [...dependsOn],
    ...verdict,
    noWork: null,
    heldOn,
  };
}

/**
 * Build one slice that lands — its disposition (`landed`) is the verifier's
 * verdict, never declared. Mirrors `plan-stories-mocks.ts`'s `merged()` helper
 * for the same reason: the row-level `status: 'merged'` is what
 * `planGroupDisposition` reads, not the other way around.
 */
function merged(groupId: string, position: number, title: string, number: number): StoryGroup {
  return group({
    groupId,
    position,
    title,
    status: 'merged',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_bulk_${groupId}`, status: 'merged', pullRequestNumber: number },
    pullRequest: {
      number,
      state: 'merged',
      title,
      mergeable: null,
      mergeDecision: 'auto',
      mergeCommitSha: null,
    },
  });
}

/** A in-flight slice, no PR yet. */
function dispatched(groupId: string, position: number, title: string): StoryGroup {
  return group({
    groupId,
    position,
    title,
    status: 'dispatched',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_bulk_${groupId}`, status: 'in_progress', pullRequestNumber: null },
    pullRequest: null,
  });
}

/** A held slice — `status: 'held'` carries the load, never the disposition verdict. */
function held(
  groupId: string,
  position: number,
  title: string,
  reason: 'area_busy' | 'upstream_group_unmerged',
): StoryGroup {
  return group({
    groupId,
    position,
    title,
    status: 'held',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_bulk_${groupId}`, status: 'pending', pullRequestNumber: null },
    pullRequest: null,
    heldOn: { reason, pullRequestNumber: null },
  });
}

/** A dead-tail slice (`task.status === 'failed'`) — what `planBucket` keys `stalled` on. */
function stalled(groupId: string, position: number, title: string): StoryGroup {
  return group({
    groupId,
    position,
    title,
    status: 'dispatched',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_bulk_${groupId}`, status: 'failed', pullRequestNumber: null },
    pullRequest: null,
  });
}

/**
 * Compose a {@link PlanStory} from the FOUR fields `planBucket` reads
 * (`status`, `mergedCount`, `stalledCount`, `heldCount`). Every other field is
 * the SAME default every hand-written story carries, so the fixture corpus is
 * the question `planBucket` answers with a bucket label.
 */
function story(input: {
  readonly id: string;
  readonly status: (typeof PlanStatus)[keyof typeof PlanStatus];
  readonly titlePrefix: string;
  readonly mergedCount: number;
  readonly stalledCount: number;
  readonly heldCount: number;
  readonly updatedIso: string;
}): PlanStory {
  const groups: StoryGroup[] = [];
  let position = 0;
  for (let i = 0; i < input.mergedCount; i++) {
    groups.push(
      merged(`grp_${i + 1}`, position, `${input.titlePrefix} (merged ${i + 1})`, 5100 + i),
    );
    position++;
  }
  for (let i = 0; i < input.stalledCount; i++) {
    groups.push(
      stalled(`grp_${position + 1}`, position, `${input.titlePrefix} (stalled ${i + 1})`),
    );
    position++;
  }
  // Held slices come last so the alphabetical group-id ordering doesn't reorder
  // the tally the way `mergedCount` expects it.
  for (let i = 0; i < input.heldCount; i++) {
    const reason = i % 2 === 0 ? 'area_busy' : 'upstream_group_unmerged';
    groups.push(
      held(`grp_${position + 1}`, position, `${input.titlePrefix} (held ${i + 1})`, reason),
    );
    position++;
  }
  // No dispatch left means no derived `in_progress` bucket here — the row is
  // either completed (every group merged) or stalled/held/closed. For a
  // genuinely in-progress row, leave one dispatched slice on the tail.
  if (
    input.mergedCount === 0 &&
    input.stalledCount === 0 &&
    input.heldCount === 0 &&
    input.status === PlanStatus.Open
  ) {
    groups.push(dispatched(`grp_${position + 1}`, position, `${input.titlePrefix} (live)`));
  }
  return {
    planId: input.id,
    status: input.status,
    rawTaskId: null,
    initiatedBy: INITIATED_BY,
    owner: OWNER,
    createdIso: input.updatedIso,
    updatedIso: input.updatedIso,
    groups,
  };
}

/**
 * 105 stories — enough that `per_page=100` leaves a second page, so the
 * dashboard's pager has SOMETHING to render and the fixture covers every
 * bucket the API ever reports.
 *
 * Bucket allocation (counted once below): 25 completed + 25 stalled +
 * 25 awaiting_approval + 25 in_progress + 5 closed = 105. Every `PlanBucket`
 * member is represented.
 */
export const BULK_PLAN_STORIES: readonly PlanStory[] = Array.from({ length: 105 }, (_, i) => {
  const slot = i + 1;
  // Slots 1-25 cover `completed`: every group merged (both arms of
  // `planBucket`'s completion rule — plan `done` OR all groups merged).
  if (slot <= 25) {
    return story({
      id: `plan_bulk_done_${String(slot).padStart(3, '0')}`,
      status: slot % 5 === 0 ? PlanStatus.Done : PlanStatus.Open,
      titlePrefix: 'Bulk done epic',
      mergedCount: 3,
      stalledCount: 0,
      heldCount: 0,
      // Newest hand-written story is 2026-07-22; we stay below so those sort first under `updatedIso` desc.
      updatedIso: at('2026-06-01'),
    });
  }
  // Slots 26-50 cover `stalled`: one dead slice outranks everything else
  // `planBucket` would otherwise return.
  if (slot <= 50) {
    return story({
      id: `plan_bulk_stalled_${String(slot - 25).padStart(3, '0')}`,
      status: PlanStatus.Open,
      titlePrefix: 'Bulk stalled epic',
      mergedCount: 1,
      stalledCount: 1,
      heldCount: 0,
      updatedIso: at('2026-05-15'),
    });
  }
  // Slots 51-75 cover `awaiting_approval`: at least one held slice, NO dead
  // slices (dead slices outrank held), so the bucket lands on `awaiting_approval`.
  if (slot <= 75) {
    return story({
      id: `plan_bulk_held_${String(slot - 50).padStart(3, '0')}`,
      status: PlanStatus.Open,
      titlePrefix: 'Bulk held epic',
      mergedCount: 0,
      stalledCount: 0,
      heldCount: 1,
      updatedIso: at('2026-05-01'),
    });
  }
  // Slots 76-100 cover `in_progress`: nothing merged, nothing stalled, nothing
  // held, plan still `open` — the only arm that lands in `in_progress` is the
  // "nothing else matched" arm of `planBucket`'s precedence.
  if (slot <= 100) {
    return story({
      id: `plan_bulk_live_${String(slot - 75).padStart(3, '0')}`,
      status: PlanStatus.Open,
      titlePrefix: 'Bulk live epic',
      mergedCount: 0,
      stalledCount: 0,
      heldCount: 0,
      updatedIso: at('2026-04-15'),
    });
  }
  // Slots 101-105 cover `closed`: a `cancelled` plan row, regardless of its
  // group counts, is `closed`.
  return story({
    id: `plan_bulk_closed_${String(slot - 100).padStart(3, '0')}`,
    status: PlanStatus.Cancelled,
    titlePrefix: 'Bulk closed epic',
    mergedCount: 1,
    stalledCount: 0,
    heldCount: 0,
    updatedIso: at('2026-04-01'),
  });
});
